/**
 * Bloom Payment Mobile App — Dynatrace console catalogue
 *
 * Pages: 52 | Events: 180
 *
 * Structure (as requested):
 * 1. Enums
 * 2. DYNATRACE_COMMON_PARAMETERS — shared SDK keys
 * 3. DYNATRACE_PAGES — all screens (p_screen)
 * 4. DYNATRACE_PAGE_EVENTS — views / clicks / errors per page
 *
 * FINAL deliverable — single file (console setup + app constants).
 * Regenerate: python3 build-dynatrace-missing-backlog.py
 */

export enum DynatraceEventKind {
    View = 'view',
    Action = 'action',
    Error = 'error',
}

export enum DynatraceAudience {
    Guest = 'guest',
    Adha = 'adha',
    SignedIn = 'signed_in',
}

/** Entity type for detail views — always send requiredProperties in SDK. */
export enum DynatraceDetailEntity {
    AdhaIdDocument = 'adha_id_document',
    AdhaLoan = 'adha_loan',
    AdhaProfile = 'adha_profile',
    AdhaProject = 'adha_project',
    AdhaUnit = 'adha_unit',
    GuestProject = 'guest_project',
    IdDocument = 'id_document',
    LandingSpace = 'landing_space',
    Loan = 'loan',
    Payment = 'payment',
    ServiceRequest = 'service_request',
    UserProfile = 'user_profile',
}

/** Shared SDK attributes — register once in Dynatrace. */
export const DYNATRACE_COMMON_PARAMETERS = {
    standard: [
        'p_screen',
        'p_actionName',
        'p_sectionName',
        'eventType',
    ],
    custom: [
        'unitId',
        'unitName',
        'unitNumber',
        'unitType',
        'projectId',
        'projectName',
        'propertyId',
        'propertyName',
        'buildingName',
        'guestUnitName',
        'location',
        'locale',
        'spaceKey',
        'spaceName',
        'experienceName',
        'adhaApplicationNumber',
        'bookingId',
        'appointmentId',
        'orderRef',
        'topUpType',
        'errorCode',
        'paymentId',
        'paymentAmount',
        'paymentStatus',
        'transactionId',
        'documentType',
        'p_failedMessage',
        'apiUrl',
        'apiMethod',
        'httpStatus',
    ],
} as const;

/** All app screens — see `DYNATRACE_PAGE_COUNT`. SDK field: `p_screen`. */
export const DYNATRACE_PAGES = {
    landingPage: {
        screen: 'landing-page',
        audience: DynatraceAudience.Guest,
        firstUserFlowOrder: 1,
    },
    landingPageSpace: {
        screen: 'landing-page-space',
        audience: DynatraceAudience.Guest,
        firstUserFlowOrder: 4,
    },
    welcome: {
        screen: 'welcome',
        audience: DynatraceAudience.SignedIn,
        firstUserFlowOrder: 7,
    },
    login: {
        screen: 'login',
        audience: DynatraceAudience.Guest,
        firstUserFlowOrder: 8,
    },
    guestHome: {
        screen: 'guest-home',
        audience: DynatraceAudience.Guest,
        firstUserFlowOrder: 9,
    },
    guest: {
        screen: 'guest',
        audience: DynatraceAudience.Guest,
        firstUserFlowOrder: 10,
    },
    guestUnit: {
        screen: 'guest',
        audience: DynatraceAudience.Guest,
        firstUserFlowOrder: 13,
    },
    almetlaaAuth: {
        screen: 'Almetlaa-auth',
        audience: DynatraceAudience.Adha,
        firstUserFlowOrder: 23,
    },
    uaePassDetails: {
        screen: 'uae-pass-details',
        audience: DynatraceAudience.SignedIn,
        firstUserFlowOrder: 40,
    },
    uaePass: {
        screen: 'uae-pass',
        audience: DynatraceAudience.SignedIn,
        firstUserFlowOrder: 41,
    },
    registrationSuccess: {
        screen: 'registration-success',
        audience: DynatraceAudience.SignedIn,
        firstUserFlowOrder: 44,
    },
    registration: {
        screen: 'registration',
        audience: DynatraceAudience.SignedIn,
        firstUserFlowOrder: 46,
    },
    documentScanner: {
        screen: 'document-scanner',
        audience: DynatraceAudience.SignedIn,
        firstUserFlowOrder: 48,
    },
    documentReview: {
        screen: 'document-review',
        audience: DynatraceAudience.SignedIn,
        firstUserFlowOrder: 52,
    },
    documentViewer: {
        screen: 'document-viewer',
        audience: DynatraceAudience.SignedIn,
        firstUserFlowOrder: 55,
    },
    otpInline: {
        screen: 'otp-inline',
        audience: DynatraceAudience.SignedIn,
        firstUserFlowOrder: 56,
    },
    passcode: {
        screen: 'passcode',
        audience: DynatraceAudience.SignedIn,
        firstUserFlowOrder: 59,
    },
    passcodeLogin: {
        screen: 'passcode-login',
        audience: DynatraceAudience.SignedIn,
        firstUserFlowOrder: 66,
    },
    forgotPassword: {
        screen: 'forgot-password',
        audience: DynatraceAudience.SignedIn,
        firstUserFlowOrder: 70,
    },
    passwordSent: {
        screen: 'password-sent',
        audience: DynatraceAudience.SignedIn,
        firstUserFlowOrder: 73,
    },
    setNewPassword: {
        screen: 'set-new-password',
        audience: DynatraceAudience.SignedIn,
        firstUserFlowOrder: 76,
    },
    changePassword: {
        screen: 'change-password',
        audience: DynatraceAudience.SignedIn,
        firstUserFlowOrder: 78,
    },
    almetlaaCatalogue: {
        screen: 'Almetlaa-catalogue',
        audience: DynatraceAudience.Adha,
        firstUserFlowOrder: 80,
    },
    almetlaaProject: {
        screen: 'Almetlaa-project',
        audience: DynatraceAudience.Adha,
        firstUserFlowOrder: 86,
    },
    almetlaaUnit: {
        screen: 'Almetlaa-unit',
        audience: DynatraceAudience.Adha,
        firstUserFlowOrder: 87,
    },
    almetlaaReserve: {
        screen: 'Almetlaa-reserve',
        audience: DynatraceAudience.Adha,
        firstUserFlowOrder: 93,
    },
    almetlaaAppointment: {
        screen: 'Almetlaa-appointment',
        audience: DynatraceAudience.Adha,
        firstUserFlowOrder: 108,
    },
    almetlaaTopUp: {
        screen: 'Almetlaa-top-up',
        audience: DynatraceAudience.Adha,
        firstUserFlowOrder: 116,
    },
    home: {
        screen: 'home',
        audience: DynatraceAudience.SignedIn,
        firstUserFlowOrder: 128,
    },
    properties: {
        screen: 'properties',
        audience: DynatraceAudience.SignedIn,
        firstUserFlowOrder: 130,
    },
    payments: {
        screen: 'payments',
        audience: DynatraceAudience.SignedIn,
        firstUserFlowOrder: 131,
    },
    propertyPayments: {
        screen: 'property_payments',
        audience: DynatraceAudience.SignedIn,
        firstUserFlowOrder: 133,
    },
    paymentFilter: {
        screen: 'payment-filter',
        audience: DynatraceAudience.SignedIn,
        firstUserFlowOrder: 136,
    },
    paymentSummary: {
        screen: 'payment_summary',
        audience: DynatraceAudience.SignedIn,
        firstUserFlowOrder: 138,
    },
    paymentCheckout: {
        screen: 'payment_checkout',
        audience: DynatraceAudience.SignedIn,
        firstUserFlowOrder: 139,
    },
    paymentDetails: {
        screen: 'payment-details',
        audience: DynatraceAudience.SignedIn,
        firstUserFlowOrder: 141,
    },
    paymentProof: {
        screen: 'payment-proof',
        audience: DynatraceAudience.SignedIn,
        firstUserFlowOrder: 143,
    },
    bankTransfer: {
        screen: 'bank-transfer',
        audience: DynatraceAudience.SignedIn,
        firstUserFlowOrder: 146,
    },
    paymentResult: {
        screen: 'payment_result',
        audience: DynatraceAudience.SignedIn,
        firstUserFlowOrder: 147,
    },
    profile: {
        screen: 'profile',
        audience: DynatraceAudience.SignedIn,
        firstUserFlowOrder: 150,
    },
    personalDetails: {
        screen: 'personal-details',
        audience: DynatraceAudience.SignedIn,
        firstUserFlowOrder: 151,
    },
    almetlaaProfile: {
        screen: 'Almetlaa-profile',
        audience: DynatraceAudience.Adha,
        firstUserFlowOrder: 152,
    },
    loanDetails: {
        screen: 'loan-details',
        audience: DynatraceAudience.SignedIn,
        firstUserFlowOrder: 153,
    },
    idProof: {
        screen: 'id-proof',
        audience: DynatraceAudience.SignedIn,
        firstUserFlowOrder: 155,
    },
    privacySecurity: {
        screen: 'privacy-security',
        audience: DynatraceAudience.SignedIn,
        firstUserFlowOrder: 157,
    },
    serviceRequest: {
        screen: 'service-request',
        audience: DynatraceAudience.SignedIn,
        firstUserFlowOrder: 159,
    },
    serviceRequestDetail: {
        screen: 'service-request-detail',
        audience: DynatraceAudience.SignedIn,
        firstUserFlowOrder: 161,
    },
    serviceRequestCreate: {
        screen: 'service-request-create',
        audience: DynatraceAudience.SignedIn,
        firstUserFlowOrder: 162,
    },
    serviceRequestSuccess: {
        screen: 'service-request-success',
        audience: DynatraceAudience.SignedIn,
        firstUserFlowOrder: 164,
    },
    almetlaaSupport: {
        screen: 'Almetlaa-support',
        audience: DynatraceAudience.Adha,
        firstUserFlowOrder: 166,
    },
    faq: {
        screen: 'faq',
        audience: DynatraceAudience.SignedIn,
        firstUserFlowOrder: 170,
    },
    almetlaaFaq: {
        screen: 'Almetlaa-faq',
        audience: DynatraceAudience.Adha,
        firstUserFlowOrder: 172,
    },
    legal: {
        screen: 'legal',
        audience: DynatraceAudience.SignedIn,
        firstUserFlowOrder: 174,
    },
    logoutSuccess: {
        screen: 'logout-success',
        audience: DynatraceAudience.SignedIn,
        firstUserFlowOrder: 176,
    },
} as const;

/** Events on each page (176 total). SDK: `p_actionName` + `eventType`. */
export const DYNATRACE_PAGE_EVENTS = {
    'landing-page': {
        'landing_page_viewed': {
            eventType: DynatraceEventKind.View,
            userFlowOrder: 1,
            description: 'Bloom experience hub screen',
        },
        'landing_page_explore_card_click': {
            eventType: DynatraceEventKind.Action,
            userFlowOrder: 2,
            description: 'Explore card — use landing_page_{spaceKey}_click',
            detailContext: {
                entity: DynatraceDetailEntity.LandingSpace,
                whatUserViewed: 'Hub mosaic tile — which space the user chose to open',
                requiredProperties: {
                    spaceKey: 'Tile space key (or use action landing_page_{spaceKey}_click)',
                    spaceName: 'Tile label',
                    locale: 'en | ar',
                },
            },
        },
        'landing_page_hub_cta_click': {
            eventType: DynatraceEventKind.Action,
            userFlowOrder: 3,
            description: 'Hub primary CTA (Contentful ctaLabel, e.g. Let\'s Get Started) — web opens welcome login; native opens /login',
        },
    },
    'landing-page-space': {
        'landing_page_space_viewed': {
            eventType: DynatraceEventKind.View,
            userFlowOrder: 4,
            description: 'space loaded — user viewing category keyed by route ?key= (see notes)',
            detailContext: {
                entity: DynatraceDetailEntity.LandingSpace,
                whatUserViewed: 'Bloom landing space / category (web /universal-space?id=, native /bloom-experience-details?key=)',
                requiredProperties: {
                    spaceKey: 'CMS space key from route (?key= or ?id=)',
                    spaceName: 'Space title shown on the page',
                    experienceName: 'Category headline (pageHeadline or title from CMS)',
                    locale: 'Active app language (en | ar)',
                },
            },
        },
        'landing_page_space_discover_more_click': {
            eventType: DynatraceEventKind.Action,
            userFlowOrder: 5,
            description: 'Hero Discover more button',
            detailContext: {
                entity: DynatraceDetailEntity.LandingSpace,
                whatUserViewed: 'On landing-page-space: user action while viewing — Bloom landing space / category (web /universal-space?id=, native /bloom-experience-details?key=)',
                requiredProperties: {
                    spaceKey: 'CMS space key from route (?key= or ?id=)',
                    spaceName: 'Space title shown on the page',
                    experienceName: 'Category headline (pageHeadline or title from CMS)',
                    locale: 'Active app language (en | ar)',
                },
            },
        },
        'landing_page_space_institution_card_click': {
            eventType: DynatraceEventKind.Action,
            userFlowOrder: 6,
            description: 'Key Institutions card tap',
            detailContext: {
                entity: DynatraceDetailEntity.LandingSpace,
                whatUserViewed: 'On landing-page-space: user action while viewing — Bloom landing space / category (web /universal-space?id=, native /bloom-experience-details?key=)',
                requiredProperties: {
                    spaceKey: 'CMS space key from route (?key= or ?id=)',
                    spaceName: 'Space title shown on the page',
                    experienceName: 'Category headline (pageHeadline or title from CMS)',
                    locale: 'Active app language (en | ar)',
                },
            },
        },
    },
    'welcome': {
        'welcome_page_viewed': {
            eventType: DynatraceEventKind.View,
            userFlowOrder: 7,
            description: 'Welcome.web mount',
        },
        'welcome_login_tap': {
            eventType: DynatraceEventKind.Action,
            userFlowOrder: 8,
            description: 'Open login dialog from welcome web',
        },
        'login_dialog_viewed': {
            eventType: DynatraceEventKind.View,
            userFlowOrder: 18,
            description: 'Login dialog opened',
        },
        'login_dialog_close': {
            eventType: DynatraceEventKind.Action,
            userFlowOrder: 19,
            description: 'User closes login dialog without completing',
        },
        'login_dialog_submit': {
            eventType: DynatraceEventKind.Action,
            userFlowOrder: 20,
            description: 'Email login submit from dialog',
        },
        'login_dialog_submit_error': {
            eventType: DynatraceEventKind.Error,
            userFlowOrder: 21,
            description: 'Email login validation/API failure',
        },
        'login_dialog_uae_pass_start': {
            eventType: DynatraceEventKind.Action,
            userFlowOrder: 22,
            description: 'UAE Pass from login dialog (non-ADHA)',
        },
    },
    'login': {
        'login_screen_viewed': {
            eventType: DynatraceEventKind.View,
            userFlowOrder: 8,
            description: 'Native /login route mount (email + UAE Pass + Emirates ID entry)',
        },
        'login_submit': {
            eventType: DynatraceEventKind.Action,
            userFlowOrder: 181,
            description: 'Email/password login submit (native login screen)',
        },
        'login_submit_error': {
            eventType: DynatraceEventKind.Error,
            userFlowOrder: 182,
            description: 'Validation or API failure on native login',
        },
        'login_success': {
            eventType: DynatraceEventKind.Action,
            userFlowOrder: 183,
            description: 'Email login API success before passcode routing',
        },
        'login_forgot_password_click': {
            eventType: DynatraceEventKind.Action,
            userFlowOrder: 184,
            description: 'Forgot password from native login',
        },
        'login_uae_pass_start': {
            eventType: DynatraceEventKind.Action,
            userFlowOrder: 185,
            description: 'Continue with UAE Pass from native login',
        },
    },
    'guest-home': {
        'guest_home_viewed': {
            eventType: DynatraceEventKind.View,
            userFlowOrder: 9,
            description: 'Guest catalogue home mount',
        },
    },
    'guest': {
        'guest_project_list_viewed': {
            eventType: DynatraceEventKind.View,
            userFlowOrder: 10,
            description: 'Guest project catalogue list visible (web /guest, native guest home)',
        },
        'guest_project_card_click': {
            eventType: DynatraceEventKind.Action,
            userFlowOrder: 11,
            description: 'Guest web project card tap',
        },
        'guest_project_detail_viewed': {
            eventType: DynatraceEventKind.View,
            userFlowOrder: 12,
            description: 'Guest marketing project detail mount (NOT an ADHA unit)',
            detailContext: {
                entity: DynatraceDetailEntity.GuestProject,
                whatUserViewed: 'Guest Contentful project detail (/guest-details?id=)',
                requiredProperties: {
                    projectId: 'Guest catalogue project id (?id= on /guest-details)',
                    projectName: 'Project marketing title on screen',
                    location: 'Location line on detail',
                    locale: 'en | ar',
                },
            },
        },
        'guest_login_prompt_view': {
            eventType: DynatraceEventKind.View,
            userFlowOrder: 16,
            description: 'Prompt to login from guest flow',
        },
        'guest_login_prompt_login_click': {
            eventType: DynatraceEventKind.Action,
            userFlowOrder: 17,
            description: 'User chooses login from guest prompt',
        },
        'guest_login_prompt_maybe_later_click': {
            eventType: DynatraceEventKind.Action,
            userFlowOrder: 17,
            description: 'User dismisses the guest login prompt with Maybe later',
        },
        'guest_login_overlay_click': {
            eventType: DynatraceEventKind.Action,
            userFlowOrder: 16,
            description: 'User taps the login overlay on the guest project list or project detail',
        },
        'guest_language_change': {
            eventType: DynatraceEventKind.Action,
            userFlowOrder: 17,
            description: 'User switches language on a guest project screen',
        },
        'guest_project_carousel_manual_change': {
            eventType: DynatraceEventKind.Action,
            userFlowOrder: 17,
            description: 'User changes the project image carousel manually (swipe, arrow, or dot)',
        },
        'guest_explore_property_click': {
            eventType: DynatraceEventKind.Action,
            userFlowOrder: 13,
            description: 'Explore panel — open another guest project',
        },
        'guest_project_brochure_download_click': {
            eventType: DynatraceEventKind.Action,
            userFlowOrder: 14,
            description: 'Download project brochure (web document row or native Documents)',
        },
        'guest_project_master_plan_view_click': {
            eventType: DynatraceEventKind.Action,
            userFlowOrder: 15,
            description: 'Open master plan / view-link document on guest project detail',
        },
        'guest_project_map_open_click': {
            eventType: DynatraceEventKind.Action,
            userFlowOrder: 16,
            description: 'Open maps for project location',
        },
        'guest_project_detail_tab_click': {
            eventType: DynatraceEventKind.Action,
            userFlowOrder: 17,
            description: 'Compact guest detail tab switch (details | explore)',
        },
        'guest_enquiry_submit': {
            eventType: DynatraceEventKind.Action,
            userFlowOrder: 18,
            description: 'Submit guest enquiry form',
        },
        'guest_enquiry_error': {
            eventType: DynatraceEventKind.Error,
            userFlowOrder: 19,
            description: 'Enquiry API failure',
        },
    },
    'Almetlaa-auth': {
        'Almetlaa-login-with-emirates-id_start_click': {
            eventType: DynatraceEventKind.Action,
            userFlowOrder: 23,
            description: 'User opens Emirates ID login sheet from native login (parity with web dialog button)',
        },
        'Almetlaa-login-with-emirates-id_view': {
            eventType: DynatraceEventKind.View,
            userFlowOrder: 24,
            description: 'Emirates ID input step shown after start_click (web LoginDialog step or native sheet)',
        },
        'Almetlaa-login-with-emirates-id_submit_click': {
            eventType: DynatraceEventKind.Action,
            userFlowOrder: 25,
            description: 'User submits Emirates ID',
        },
        'Almetlaa-login-with-emirates-id_error': {
            eventType: DynatraceEventKind.Error,
            userFlowOrder: 26,
            description: 'Emirates ID API/validation failure',
        },
        'Almetlaa-mobile-otp_view': {
            eventType: DynatraceEventKind.View,
            userFlowOrder: 27,
            description: 'Mobile OTP step displayed',
        },
        'Almetlaa-mobile-otp_verify_click': {
            eventType: DynatraceEventKind.Action,
            userFlowOrder: 27,
            description: 'User taps Verify on phone (SMS) OTP step (Emirates ID login)',
        },
        'Almetlaa-mobile-otp_verify_success': {
            eventType: DynatraceEventKind.Action,
            userFlowOrder: 28,
            description: 'Mobile OTP verified',
        },
        'Almetlaa-mobile-otp_verify_error': {
            eventType: DynatraceEventKind.Error,
            userFlowOrder: 29,
            description: 'Mobile OTP verify failed',
        },
        'Almetlaa-email-otp_view': {
            eventType: DynatraceEventKind.View,
            userFlowOrder: 30,
            description: 'Email OTP step displayed',
        },
        'Almetlaa-email-otp_verify_click': {
            eventType: DynatraceEventKind.Action,
            userFlowOrder: 30,
            description: 'User taps Verify on email OTP step (Emirates ID login)',
        },
        'Almetlaa-email-otp_verify_success': {
            eventType: DynatraceEventKind.Action,
            userFlowOrder: 31,
            description: 'Email OTP verified',
        },
        'Almetlaa-email-otp_verify_error': {
            eventType: DynatraceEventKind.Error,
            userFlowOrder: 32,
            description: 'Email OTP verify failed',
        },
        'Almetlaa-mobile-otp_resend_click': {
            eventType: DynatraceEventKind.Action,
            userFlowOrder: 33,
            description: 'User taps resend on mobile OTP (Emirates ID login)',
        },
        'Almetlaa-mobile-otp_resend_error': {
            eventType: DynatraceEventKind.Error,
            userFlowOrder: 34,
            description: 'Mobile OTP resend API failure',
        },
        'Almetlaa-email-otp_resend_click': {
            eventType: DynatraceEventKind.Action,
            userFlowOrder: 35,
            description: 'User taps resend on email OTP (Emirates ID login)',
        },
        'Almetlaa-email-otp_resend_error': {
            eventType: DynatraceEventKind.Error,
            userFlowOrder: 36,
            description: 'Email OTP resend API failure',
        },
        'Almetlaa-login-otp_raise_support_click': {
            eventType: DynatraceEventKind.Action,
            userFlowOrder: 37,
            description: 'User opens service request from Emirates OTP (no access to phone / raise ticket)',
        },
        'Almetlaa-uae-pass-oauth_start_click': {
            eventType: DynatraceEventKind.Action,
            userFlowOrder: 38,
            description: 'User starts ADHA UAE Pass OAuth',
        },
        'Almetlaa-uae-pass-callback_view': {
            eventType: DynatraceEventKind.View,
            userFlowOrder: 39,
            description: 'Native ADHA UAE Pass WebView screen mount',
        },
        'Almetlaa-uae-pass-oauth_success': {
            eventType: DynatraceEventKind.Action,
            userFlowOrder: 40,
            description: 'OAuth callback success',
        },
        'Almetlaa-uae-pass-oauth_error': {
            eventType: DynatraceEventKind.Error,
            userFlowOrder: 41,
            description: 'OAuth callback error',
        },
        'Almetlaa-uae-pass-oauth_cancel': {
            eventType: DynatraceEventKind.Action,
            userFlowOrder: 42,
            description: 'User cancels OAuth',
        },
        'Almetlaa-adha-session_success': {
            eventType: DynatraceEventKind.Action,
            userFlowOrder: 43,
            description: 'ADHA catalogue session established after login',
        },
        'Almetlaa-adha-session_error': {
            eventType: DynatraceEventKind.Error,
            userFlowOrder: 44,
            description: 'ADHA login/session hydration failed',
        },
    },
    'uae-pass-details': {
        'uae_pass_flow_viewed': {
            eventType: DynatraceEventKind.View,
            userFlowOrder: 40,
            description: 'Registration UAE Pass details mount',
        },
    },
    'uae-pass': {
        'uae_pass_login_success': {
            eventType: DynatraceEventKind.Action,
            userFlowOrder: 41,
            description: 'Non-ADHA UAE Pass login/register success',
        },
        'uae_pass_login_error': {
            eventType: DynatraceEventKind.Error,
            userFlowOrder: 42,
            description: 'Non-ADHA OAuth error',
        },
        'uae_pass_login_cancelled': {
            eventType: DynatraceEventKind.Action,
            userFlowOrder: 43,
            description: 'User cancelled OAuth',
        },
    },
    'registration-success': {
        'registration_success_viewed': {
            eventType: DynatraceEventKind.View,
            userFlowOrder: 44,
            description: 'Success screen mount',
        },
        'registration_success_continue': {
            eventType: DynatraceEventKind.Action,
            userFlowOrder: 45,
            description: 'Continue CTA after registration',
        },
    },
    'registration': {
        'current_password_screen_viewed': {
            eventType: DynatraceEventKind.View,
            userFlowOrder: 46,
            description: 'Set password during registration',
        },
        'password_set_screen_submit': {
            eventType: DynatraceEventKind.Action,
            userFlowOrder: 47,
            description: 'Submit new password',
        },
    },
    'document-scanner': {
        'document_scanner_viewed': {
            eventType: DynatraceEventKind.View,
            userFlowOrder: 48,
            description: 'Scanner mount',
        },
        'document_capture_success': {
            eventType: DynatraceEventKind.Action,
            userFlowOrder: 49,
            description: 'Capture completed',
        },
        'document_capture_error': {
            eventType: DynatraceEventKind.Error,
            userFlowOrder: 50,
            description: 'Camera/permission failure',
        },
        'document_scanner_confirm': {
            eventType: DynatraceEventKind.Action,
            userFlowOrder: 51,
            description: 'Confirm capture → review',
        },
    },
    'document-review': {
        'document_review_viewed': {
            eventType: DynatraceEventKind.View,
            userFlowOrder: 52,
            description: 'Review mount',
        },
        'document_review_edit': {
            eventType: DynatraceEventKind.Action,
            userFlowOrder: 53,
            description: 'Retake / edit document',
        },
        'document_review_confirm': {
            eventType: DynatraceEventKind.Action,
            userFlowOrder: 54,
            description: 'Confirm extracted data',
        },
    },
    'document-viewer': {
        'document_viewer_viewed': {
            eventType: DynatraceEventKind.View,
            userFlowOrder: 55,
            description: 'Open uploaded document',
        },
    },
    'otp-inline': {
        'inline_otp_viewed': {
            eventType: DynatraceEventKind.View,
            userFlowOrder: 56,
            description: 'Profile/login inline OTP mount',
        },
        'inline_otp_verify_submit': {
            eventType: DynatraceEventKind.Action,
            userFlowOrder: 57,
            description: 'Verify OTP submit',
        },
        'inline_otp_resend_click': {
            eventType: DynatraceEventKind.Action,
            userFlowOrder: 58,
            description: 'Resend OTP',
        },
    },
    'passcode': {
        'passcode_setup_viewed': {
            eventType: DynatraceEventKind.View,
            userFlowOrder: 59,
            description: 'Mount mode=setup',
        },
        'passcode_set_success': {
            eventType: DynatraceEventKind.Action,
            userFlowOrder: 60,
            description: 'Passcode saved successfully',
        },
        'passcode_set_error': {
            eventType: DynatraceEventKind.Error,
            userFlowOrder: 61,
            description: 'Save passcode failed',
        },
        'passcode_biometric_skip': {
            eventType: DynatraceEventKind.Action,
            userFlowOrder: 62,
            description: 'User skips biometric enable on setup',
        },
        'passcode_verify_viewed': {
            eventType: DynatraceEventKind.View,
            userFlowOrder: 63,
            description: 'Mount mode=verify',
        },
        'passcode_verify_success': {
            eventType: DynatraceEventKind.Action,
            userFlowOrder: 64,
            description: 'Verify success',
        },
        'passcode_verify_error': {
            eventType: DynatraceEventKind.Error,
            userFlowOrder: 65,
            description: 'Wrong passcode',
        },
        'passcode_change_viewed': {
            eventType: DynatraceEventKind.View,
            userFlowOrder: 69,
            description: 'Mount mode=change',
        },
    },
    'passcode-login': {
        'passcode_login_viewed': {
            eventType: DynatraceEventKind.View,
            userFlowOrder: 66,
            description: 'Mount',
        },
        'passcode_login_submit': {
            eventType: DynatraceEventKind.Action,
            userFlowOrder: 67,
            description: 'Passcode verified successfully',
        },
        'passcode_login_error': {
            eventType: DynatraceEventKind.Error,
            userFlowOrder: 68,
            description: 'Wrong passcode / API error',
        },
    },
    'forgot-password': {
        'forgot_password_viewed': {
            eventType: DynatraceEventKind.View,
            userFlowOrder: 70,
            description: 'Route mount',
        },
        'forgot_password_send_link': {
            eventType: DynatraceEventKind.Action,
            userFlowOrder: 71,
            description: 'sendResetLink success',
        },
        'forgot_password_send_link_error': {
            eventType: DynatraceEventKind.Error,
            userFlowOrder: 72,
            description: 'sendResetLink failure',
        },
        'forgot_password_otp_verify_success': {
            eventType: DynatraceEventKind.Action,
            userFlowOrder: 74,
            description: 'Recovery OTP verified',
        },
        'forgot_password_otp_verify_error': {
            eventType: DynatraceEventKind.Error,
            userFlowOrder: 75,
            description: 'Recovery OTP failed',
        },
    },
    'password-sent': {
        'password_sent_viewed': {
            eventType: DynatraceEventKind.View,
            userFlowOrder: 73,
            description: 'Confirmation screen mount',
        },
    },
    'set-new-password': {
        'forgot_password_set_success': {
            eventType: DynatraceEventKind.Action,
            userFlowOrder: 76,
            description: 'New password saved',
        },
        'forgot_password_set_password_error': {
            eventType: DynatraceEventKind.Error,
            userFlowOrder: 77,
            description: 'Set password API failure',
        },
    },
    'change-password': {
        'change_password_viewed': {
            eventType: DynatraceEventKind.View,
            userFlowOrder: 78,
            description: 'Mount',
        },
        'password_change_success': {
            eventType: DynatraceEventKind.Action,
            userFlowOrder: 79,
            description: 'changePassword API success',
        },
        'password_change_failed': {
            eventType: DynatraceEventKind.Error,
            userFlowOrder: 80,
            description: 'changePassword catch — success already implemented',
        },
    },
    'Almetlaa-catalogue': {
        'Almetlaa_catalogue_home_view': {
            eventType: DynatraceEventKind.View,
            userFlowOrder: 80,
            description: 'ADHA catalogue listing mount (replaces HomeScreen on web)',
        },
        'Almetlaa_catalogue_filter_apply_click': {
            eventType: DynatraceEventKind.Action,
            userFlowOrder: 81,
            description: 'User applies listing filters',
        },
        'Almetlaa_catalogue_page_change': {
            eventType: DynatraceEventKind.Action,
            userFlowOrder: 82,
            description: 'Pagination / load more',
        },
        'Almetlaa_catalogue_price_filter_open': {
            eventType: DynatraceEventKind.Action,
            userFlowOrder: 83,
            description: 'Open price range filter sheet',
        },
        'Almetlaa_catalogue_price_filter_apply': {
            eventType: DynatraceEventKind.Action,
            userFlowOrder: 84,
            description: 'Apply price filter',
        },
        'Almetlaa_project_card_click': {
            eventType: DynatraceEventKind.Action,
            userFlowOrder: 85,
            description: 'User taps a project card. Guest listing uses guest_project_card_click. Logged-in unit cards use Almetlaa_unit_card_click.',
        },
        'Almetlaa_unit_card_click': {
            eventType: DynatraceEventKind.Action,
            userFlowOrder: 85,
            description: 'Logged-in ADHA user taps a unit card on the catalogue',
        },
        'Almetlaa_reservation_card_click': {
            eventType: DynatraceEventKind.Action,
            userFlowOrder: 85,
            description: 'Logged-in ADHA user taps the reservation status card',
        },
        'Almetlaa_appointment_card_click': {
            eventType: DynatraceEventKind.Action,
            userFlowOrder: 85,
            description: 'Logged-in ADHA user taps the appointment status card',
        },
    },
    'Almetlaa-project': {
        'Almetlaa_project_{projectId}_detail_page': {
            eventType: DynatraceEventKind.View,
            userFlowOrder: 86,
            description: 'Project detail mount — use ALMETLAA_ACTION.UNIT.projectDetailPage(projectId)',
            detailContext: {
                entity: DynatraceDetailEntity.AdhaProject,
                whatUserViewed: 'ADHA authenticated project detail',
                requiredProperties: {
                    projectId: 'Salesforce / ADHA project id',
                    projectName: 'Project name on screen',
                    locale: 'en | ar',
                },
            },
        },
    },
    'Almetlaa-unit': {
        'Almetlaa_unit_{unitId}_detail_page': {
            eventType: DynatraceEventKind.View,
            userFlowOrder: 87,
            description: 'Replace generic Property Details Viewed for ADHA-only actions on sticky footer CTAs',
            detailContext: {
                entity: DynatraceDetailEntity.AdhaUnit,
                whatUserViewed: 'ADHA authenticated unit detail',
                requiredProperties: {
                    unitId: 'Unit id in route / API',
                    unitName: 'Display name (project + unit number / type)',
                    projectId: 'Parent project id',
                    projectName: 'Parent project name',
                    unitNumber: 'Unit number if shown',
                    unitType: 'Unit type label if shown',
                    locale: 'en | ar',
                },
            },
        },
        'Almetlaa_unit_{unitId}_gallery_open_click': {
            eventType: DynatraceEventKind.Action,
            userFlowOrder: 88,
            description: 'Open image gallery',
            detailContext: {
                entity: DynatraceDetailEntity.AdhaUnit,
                whatUserViewed: 'On Almetlaa-unit: user action while viewing — ADHA authenticated unit detail',
                requiredProperties: {
                    unitId: 'Unit id in route / API',
                    unitName: 'Display name (project + unit number / type)',
                    projectId: 'Parent project id',
                    projectName: 'Parent project name',
                    unitNumber: 'Unit number if shown',
                    unitType: 'Unit type label if shown',
                    locale: 'en | ar',
                },
            },
        },
        'Almetlaa_unit_{unitId}_brochure_download_click': {
            eventType: DynatraceEventKind.Action,
            userFlowOrder: 89,
            description: 'Download brochure',
            detailContext: {
                entity: DynatraceDetailEntity.AdhaUnit,
                whatUserViewed: 'On Almetlaa-unit: user action while viewing — ADHA authenticated unit detail',
                requiredProperties: {
                    unitId: 'Unit id in route / API',
                    unitName: 'Display name (project + unit number / type)',
                    projectId: 'Parent project id',
                    projectName: 'Parent project name',
                    unitNumber: 'Unit number if shown',
                    unitType: 'Unit type label if shown',
                    locale: 'en | ar',
                },
            },
        },
        'Almetlaa_unit_{unitId}_share_click': {
            eventType: DynatraceEventKind.Action,
            userFlowOrder: 90,
            description: 'Share unit',
            detailContext: {
                entity: DynatraceDetailEntity.AdhaUnit,
                whatUserViewed: 'On Almetlaa-unit: user action while viewing — ADHA authenticated unit detail',
                requiredProperties: {
                    unitId: 'Unit id in route / API',
                    unitName: 'Display name (project + unit number / type)',
                    projectId: 'Parent project id',
                    projectName: 'Parent project name',
                    unitNumber: 'Unit number if shown',
                    unitType: 'Unit type label if shown',
                    locale: 'en | ar',
                },
            },
        },
        'Almetlaa_unit_{unitId}_book_appointment_click': {
            eventType: DynatraceEventKind.Action,
            userFlowOrder: 91,
            description: 'Sticky Book appointment button native',
            detailContext: {
                entity: DynatraceDetailEntity.AdhaUnit,
                whatUserViewed: 'On Almetlaa-unit: user action while viewing — ADHA authenticated unit detail',
                requiredProperties: {
                    unitId: 'Unit id in route / API',
                    unitName: 'Display name (project + unit number / type)',
                    projectId: 'Parent project id',
                    projectName: 'Parent project name',
                    unitNumber: 'Unit number if shown',
                    unitType: 'Unit type label if shown',
                    locale: 'en | ar',
                },
            },
        },
        'Almetlaa_unit_{unitId}_reserve_click': {
            eventType: DynatraceEventKind.Action,
            userFlowOrder: 92,
            description: 'Sticky Reserve button native',
            detailContext: {
                entity: DynatraceDetailEntity.AdhaUnit,
                whatUserViewed: 'On Almetlaa-unit: user action while viewing — ADHA authenticated unit detail',
                requiredProperties: {
                    unitId: 'Unit id in route / API',
                    unitName: 'Display name (project + unit number / type)',
                    projectId: 'Parent project id',
                    projectName: 'Parent project name',
                    unitNumber: 'Unit number if shown',
                    unitType: 'Unit type label if shown',
                    locale: 'en | ar',
                },
            },
        },
        'Almetlaa_reservation_summary_open': {
            eventType: DynatraceEventKind.Action,
            userFlowOrder: 104,
            description: 'Open reservation summary sheet',
            detailContext: {
                entity: DynatraceDetailEntity.AdhaUnit,
                whatUserViewed: 'On Almetlaa-unit: user action while viewing — ADHA authenticated unit detail',
                requiredProperties: {
                    unitId: 'Unit id in route / API',
                    unitName: 'Display name (project + unit number / type)',
                    projectId: 'Parent project id',
                    projectName: 'Parent project name',
                    unitNumber: 'Unit number if shown',
                    unitType: 'Unit type label if shown',
                    locale: 'en | ar',
                },
            },
        },
        'Almetlaa_unit_next_step_sheet_open': {
            eventType: DynatraceEventKind.Action,
            userFlowOrder: 107,
            description: 'Post-action next steps sheet',
            detailContext: {
                entity: DynatraceDetailEntity.AdhaUnit,
                whatUserViewed: 'On Almetlaa-unit: user action while viewing — ADHA authenticated unit detail',
                requiredProperties: {
                    unitId: 'Unit id in route / API',
                    unitName: 'Display name (project + unit number / type)',
                    projectId: 'Parent project id',
                    projectName: 'Parent project name',
                    unitNumber: 'Unit number if shown',
                    unitType: 'Unit type label if shown',
                    locale: 'en | ar',
                },
            },
        },
    },
    'Almetlaa-reserve': {
        'Almetlaa_reserve_unit_page_view': {
            eventType: DynatraceEventKind.View,
            userFlowOrder: 93,
            description: 'Screen mount',
        },
        'Almetlaa_reserve_unit_confirm_click': {
            eventType: DynatraceEventKind.Action,
            userFlowOrder: 94,
            description: 'Confirm reservation button',
        },
        'Almetlaa_reserve_unit_success': {
            eventType: DynatraceEventKind.Action,
            userFlowOrder: 95,
            description: 'bookingService reserve API success',
        },
        'Almetlaa_reserve_unit_error': {
            eventType: DynatraceEventKind.Error,
            userFlowOrder: 96,
            description: 'Reserve API failure',
        },
        'Almetlaa_reserve_unit_already_reserved_error': {
            eventType: DynatraceEventKind.Error,
            userFlowOrder: 97,
            description: 'Already reserved API/state',
        },
        'Almetlaa_confirm_reservation_dialog_view': {
            eventType: DynatraceEventKind.View,
            userFlowOrder: 98,
            description: 'Confirm reservation modal shown',
        },
        'Almetlaa_confirm_reservation_dialog_confirm': {
            eventType: DynatraceEventKind.Action,
            userFlowOrder: 99,
            description: 'User confirms reservation in dialog',
        },
        'Almetlaa_confirm_reservation_dialog_cancel': {
            eventType: DynatraceEventKind.Action,
            userFlowOrder: 99,
            description: 'User dismisses the confirm reservation dialog',
        },
        'Almetlaa_unit_reserved_dialog_view': {
            eventType: DynatraceEventKind.View,
            userFlowOrder: 100,
            description: 'Shared unit reserved dialog',
        },
        'Almetlaa_unit_reserved_success_dialog_view': {
            eventType: DynatraceEventKind.View,
            userFlowOrder: 101,
            description: 'Success after reserve',
        },
        'Almetlaa_unit_reserved_success_primary_click': {
            eventType: DynatraceEventKind.Action,
            userFlowOrder: 102,
            description: 'Primary button on unit reserved success dialog (use button title as label if needed)',
        },
        'Almetlaa_reservation_canceled_dialog_view': {
            eventType: DynatraceEventKind.View,
            userFlowOrder: 103,
            description: 'Reservation canceled confirmation',
        },
        'Almetlaa_cancel_reservation_dialog_open': {
            eventType: DynatraceEventKind.Action,
            userFlowOrder: 105,
            description: 'User starts cancel flow',
        },
        'Almetlaa_cancel_reservation_confirm': {
            eventType: DynatraceEventKind.Action,
            userFlowOrder: 106,
            description: 'Confirm cancel reservation',
        },
        'Almetlaa_cancel_reservation_dialog_no': {
            eventType: DynatraceEventKind.Action,
            userFlowOrder: 106,
            description: 'No on the cancel reservation dialog',
        },
    },
    'Almetlaa-appointment': {
        'Almetlaa_book_appointment_page_view': {
            eventType: DynatraceEventKind.View,
            userFlowOrder: 108,
            description: 'Screen mount',
        },
        'Almetlaa_book_appointment_slot_select_click': {
            eventType: DynatraceEventKind.Action,
            userFlowOrder: 109,
            description: 'Select time slot',
        },
        'Almetlaa_appointment_slot_dialog_open': {
            eventType: DynatraceEventKind.Action,
            userFlowOrder: 110,
            description: 'Open slot picker dialog',
        },
        'Almetlaa_reschedule_appointment_click': {
            eventType: DynatraceEventKind.Action,
            userFlowOrder: 110,
            description: 'Logged-in ADHA user taps Reschedule on an existing appointment',
        },
        'Almetlaa_appointment_date_scroll_click': {
            eventType: DynatraceEventKind.Action,
            userFlowOrder: 110,
            description: 'Scroll the appointment date strip',
        },
        'Almetlaa_appointment_date_select_click': {
            eventType: DynatraceEventKind.Action,
            userFlowOrder: 110,
            description: 'Pick a date to load appointment slots',
        },
        'Almetlaa_appointment_slot_dialog_cancel': {
            eventType: DynatraceEventKind.Action,
            userFlowOrder: 110,
            description: 'Cancel or close the appointment slot dialog',
        },
        'Almetlaa_book_appointment_click': {
            eventType: DynatraceEventKind.Action,
            userFlowOrder: 111,
            description: 'Submit appointment (Almetlaa_Book_Appointment_click)',
        },
        'Almetlaa_book_appointment_success': {
            eventType: DynatraceEventKind.Action,
            userFlowOrder: 112,
            description: 'Appointment booked successfully',
        },
        'Almetlaa_book_appointment_error': {
            eventType: DynatraceEventKind.Error,
            userFlowOrder: 113,
            description: 'Appointment booking failed',
        },
        'Almetlaa_appointment_confirmed_dialog_view': {
            eventType: DynatraceEventKind.View,
            userFlowOrder: 114,
            description: 'Appointment success dialog',
        },
        'Almetlaa_cancel_appointment_dialog_confirm': {
            eventType: DynatraceEventKind.Action,
            userFlowOrder: 115,
            description: 'Confirm cancel appointment',
        },
        'Almetlaa_cancel_appointment_dialog_no': {
            eventType: DynatraceEventKind.Action,
            userFlowOrder: 115,
            description: 'No on the cancel appointment dialog',
        },
        'Almetlaa_cancel_appointment_request_click': {
            eventType: DynatraceEventKind.Action,
            userFlowOrder: 115,
            description: 'User starts cancel appointment from the actions menu',
        },
    },
    'Almetlaa-top-up': {
        'Almetlaa_complete_payment_page_view': {
            eventType: DynatraceEventKind.View,
            userFlowOrder: 116,
            description: 'Complete your payment screen mount',
        },
        'Almetlaa_bank_transfer_select_click': {
            eventType: DynatraceEventKind.Action,
            userFlowOrder: 117,
            description: 'Select bank transfer method',
        },
        'Almetlaa_proof_of_mortgage_select_click': {
            eventType: DynatraceEventKind.Action,
            userFlowOrder: 118,
            description: 'Select proof of mortgage path',
        },
        'Almetlaa_bank_transfer_details_view': {
            eventType: DynatraceEventKind.View,
            userFlowOrder: 119,
            description: 'Bank transfer details mount',
        },
        'Almetlaa_payment_proof_submit_click': {
            eventType: DynatraceEventKind.Action,
            userFlowOrder: 120,
            description: 'Submit payment proof',
        },
        'Almetlaa_top_up_submit_success': {
            eventType: DynatraceEventKind.Action,
            userFlowOrder: 121,
            description: 'Top-up flow completed',
        },
        'Almetlaa_top_up_submit_error': {
            eventType: DynatraceEventKind.Error,
            userFlowOrder: 122,
            description: 'Top-up submission failed',
        },
        'Almetlaa_complete_payment_sheet_open': {
            eventType: DynatraceEventKind.Action,
            userFlowOrder: 123,
            description: 'Open complete payment bottom sheet',
        },
        'Almetlaa_complete_payment_dialog_view': {
            eventType: DynatraceEventKind.View,
            userFlowOrder: 124,
            description: 'Complete payment modal',
        },
        'Almetlaa_complete_payment_dialog_confirm': {
            eventType: DynatraceEventKind.Action,
            userFlowOrder: 125,
            description: 'Confirm payment method in dialog',
        },
        'Almetlaa_complete_payment_dialog_cancel': {
            eventType: DynatraceEventKind.Action,
            userFlowOrder: 125,
            description: 'Cancel on the complete payment dialog',
        },
        'Almetlaa_bank_transfer_made_click': {
            eventType: DynatraceEventKind.Action,
            userFlowOrder: 126,
            description: 'User taps I have made the transfer',
        },
        'Almetlaa_bank_transfer_dialog_view': {
            eventType: DynatraceEventKind.View,
            userFlowOrder: 126,
            description: 'Bank transfer info dialog',
        },
        'Almetlaa_payment_proof_received_dialog_view': {
            eventType: DynatraceEventKind.View,
            userFlowOrder: 127,
            description: 'Proof submission acknowledged',
        },
    },
    'home': {
        'home_view_all_properties_click': {
            eventType: DynatraceEventKind.Action,
            userFlowOrder: 128,
            description: 'Navigate to properties list',
        },
        'home_pay_all_click': {
            eventType: DynatraceEventKind.Action,
            userFlowOrder: 129,
            description: 'Pay all missed payments CTA if not tracked',
        },
    },
    'properties': {
        'my_properties_viewed': {
            eventType: DynatraceEventKind.View,
            userFlowOrder: 130,
            description: 'Mount — parity with native PropertiesScreen',
        },
    },
    'payments': {
        'payments_tab_viewed': {
            eventType: DynatraceEventKind.View,
            userFlowOrder: 131,
            description: 'Payments tab mount (web + native)',
        },
        'payments_tab_property_select': {
            eventType: DynatraceEventKind.Action,
            userFlowOrder: 132,
            description: 'Select property on payments tab',
        },
    },
    'property_payments': {
        'payments_page_viewed': {
            eventType: DynatraceEventKind.View,
            userFlowOrder: 133,
            description: 'Mount — parity with native PropertyPaymentsScreen',
        },
        'property_payments_tab_switch': {
            eventType: DynatraceEventKind.Action,
            userFlowOrder: 134,
            description: 'Switch missed/upcoming tabs',
        },
        'property_payments_pay_now': {
            eventType: DynatraceEventKind.Action,
            userFlowOrder: 135,
            description: 'Pay now from web property payments',
        },
        'property_payments_item_toggle': {
            eventType: DynatraceEventKind.Action,
            userFlowOrder: 198,
            description: 'Select or deselect a payment row (checkbox)',
        },
        'property_payments_section_toggle': {
            eventType: DynatraceEventKind.Action,
            userFlowOrder: 199,
            description: 'Select or deselect all payments in a section',
        },
        'property_payments_payment_detail_open': {
            eventType: DynatraceEventKind.Action,
            userFlowOrder: 200,
            description: 'Open payment detail (native route or web dialog)',
        },
    },
    'payment-filter': {
        'payment_filter_viewed': {
            eventType: DynatraceEventKind.View,
            userFlowOrder: 136,
            description: 'Filter screen mount',
        },
        'payment_filter_apply': {
            eventType: DynatraceEventKind.Action,
            userFlowOrder: 137,
            description: 'Apply filters',
        },
    },
    'payment_summary': {
        'payment_summary_viewed': {
            eventType: DynatraceEventKind.View,
            userFlowOrder: 137,
            description: 'Payment summary screen mount',
        },
        'payment_summary_pay_now_click': {
            eventType: DynatraceEventKind.Action,
            userFlowOrder: 138,
            description: 'Proceed to checkout — not tracked today',
        },
        'payment_summary_checkout_initiated': {
            eventType: DynatraceEventKind.Action,
            userFlowOrder: 201,
            description: 'Checkout API success — navigating to gateway',
        },
        'payment_summary_checkout_error': {
            eventType: DynatraceEventKind.Error,
            userFlowOrder: 202,
            description: 'Checkout API or client failure',
        },
        'payment_summary_revenue_mid_missing': {
            eventType: DynatraceEventKind.Error,
            userFlowOrder: 203,
            description: 'Split missing revenue_MID in unit metadata',
        },
    },
    'payment_checkout': {
        'payment_checkout_viewed': {
            eventType: DynatraceEventKind.View,
            userFlowOrder: 139,
            description: 'Checkout WebView/redirect screen mount',
        },
        'payment_checkout_webview_error': {
            eventType: DynatraceEventKind.Error,
            userFlowOrder: 140,
            description: 'WebView load/navigation error',
        },
    },
    'payment-details': {
        'payment_details_screen_viewed': {
            eventType: DynatraceEventKind.View,
            userFlowOrder: 141,
            description: 'Single payment detail mount',
            detailContext: {
                entity: DynatraceDetailEntity.Payment,
                whatUserViewed: 'Single payment / instalment detail',
                requiredProperties: {
                    paymentId: 'Payment or instalment id',
                    propertyId: 'Owned property id when applicable',
                    propertyName: 'Property name on context',
                    paymentAmount: 'Amount shown',
                    paymentStatus: 'Status chip value',
                },
            },
        },
        'payment_details_dialog_open': {
            eventType: DynatraceEventKind.Action,
            userFlowOrder: 142,
            description: 'Open payment details modal from list',
        },
    },
    'payment-proof': {
        'payment_proof_submit_viewed': {
            eventType: DynatraceEventKind.View,
            userFlowOrder: 143,
            description: 'Mount',
        },
        'payment_proof_submit': {
            eventType: DynatraceEventKind.Action,
            userFlowOrder: 144,
            description: 'Submit proof (normal journey)',
        },
        'payment_proof_submit_error': {
            eventType: DynatraceEventKind.Error,
            userFlowOrder: 145,
            description: 'Upload/API failure',
        },
    },
    'bank-transfer': {
        'bank_transfer_details_viewed': {
            eventType: DynatraceEventKind.View,
            userFlowOrder: 146,
            description: 'Normal (non-ADHA) bank transfer info',
        },
    },
    'payment_result': {
        'payment_result_viewed': {
            eventType: DynatraceEventKind.View,
            userFlowOrder: 197,
            description: 'Payment gateway result shown (SUCCESS/FAILED/CANCELLED/PENDING)',
        },
        'payment_result_primary_action': {
            eventType: DynatraceEventKind.Action,
            userFlowOrder: 147,
            description: 'Back to home / retry primary button',
        },
        'payment_result_secondary_action': {
            eventType: DynatraceEventKind.Action,
            userFlowOrder: 148,
            description: 'Secondary CTA (e.g. view payments)',
        },
        'payment_result_copy_transaction_id': {
            eventType: DynatraceEventKind.Action,
            userFlowOrder: 149,
            description: 'Copy transaction ID tap',
        },
        'payment_result_report_issue': {
            eventType: DynatraceEventKind.Action,
            userFlowOrder: 150,
            description: 'Report issue link',
        },
    },
    'profile': {
        'profile_viewed': {
            eventType: DynatraceEventKind.View,
            userFlowOrder: 150,
            description: 'Profile tab / my profile mount (mobile tab + web dropdown entry)',
        },
        'profile_logout_press': {
            eventType: DynatraceEventKind.Action,
            userFlowOrder: 177,
            description: 'User taps Log out',
        },
        'profile_logout_confirm': {
            eventType: DynatraceEventKind.Action,
            userFlowOrder: 178,
            description: 'User confirms logout in dialog',
        },
        'profile_logout_success': {
            eventType: DynatraceEventKind.Action,
            userFlowOrder: 179,
            description: 'Session cleared after logout (all platforms, including web toast flow)',
        },
        'profile_logout_error': {
            eventType: DynatraceEventKind.Error,
            userFlowOrder: 180,
            description: 'Logout failed (network or clearAuth error)',
        },
        'profile_open_personal_details': {
            eventType: DynatraceEventKind.Action,
            userFlowOrder: 186,
            description: 'Navigate to personal details',
        },
        'profile_open_loan_details': {
            eventType: DynatraceEventKind.Action,
            userFlowOrder: 187,
            description: 'Navigate to loan details',
        },
        'profile_open_id_proof': {
            eventType: DynatraceEventKind.Action,
            userFlowOrder: 188,
            description: 'Navigate to ID proofs',
        },
        'profile_open_privacy_security': {
            eventType: DynatraceEventKind.Action,
            userFlowOrder: 189,
            description: 'Navigate to privacy & security',
        },
        'profile_open_faq': {
            eventType: DynatraceEventKind.Action,
            userFlowOrder: 190,
            description: 'Navigate to FAQ from profile',
        },
        'profile_open_service_request': {
            eventType: DynatraceEventKind.Action,
            userFlowOrder: 191,
            description: 'Navigate to customer support list',
        },
        'profile_copy_customer_id': {
            eventType: DynatraceEventKind.Action,
            userFlowOrder: 192,
            description: 'Copy customer / ADHA application id',
        },
        'profile_delete_account_press': {
            eventType: DynatraceEventKind.Action,
            userFlowOrder: 193,
            description: 'Open delete/deactivate account dialog',
        },
        'profile_delete_account_started': {
            eventType: DynatraceEventKind.Action,
            userFlowOrder: 203,
            description: 'delete-user API call started (after user confirmed)',
        },
        'profile_delete_account_success': {
            eventType: DynatraceEventKind.Action,
            userFlowOrder: 204,
            description: 'delete-user API success before logout redirect',
        },
        'profile_delete_account_error': {
            eventType: DynatraceEventKind.Error,
            userFlowOrder: 205,
            description: 'delete-user API or flow failure',
        },
    },
    'personal-details': {
        'personal_details_viewed': {
            eventType: DynatraceEventKind.View,
            userFlowOrder: 151,
            description: 'Mount when !isAdhaJourney',
            detailContext: {
                entity: DynatraceDetailEntity.UserProfile,
                whatUserViewed: 'Signed-in user personal details',
                requiredProperties: {
                    adhaApplicationNumber: 'When ADHA profile',
                    locale: 'en | ar',
                },
            },
        },
    },
    'Almetlaa-profile': {
        'Almetlaa_personal_details_view': {
            eventType: DynatraceEventKind.View,
            userFlowOrder: 152,
            description: 'Mount when isAdhaJourney',
            detailContext: {
                entity: DynatraceDetailEntity.AdhaProfile,
                whatUserViewed: 'ADHA personal details',
                requiredProperties: {
                    adhaApplicationNumber: 'ADHA application number',
                    locale: 'en | ar',
                },
            },
        },
        'Almetlaa_loan_details_view': {
            eventType: DynatraceEventKind.View,
            userFlowOrder: 154,
            description: 'ADHA loan details when isAdhaJourney',
            detailContext: {
                entity: DynatraceDetailEntity.AdhaLoan,
                whatUserViewed: 'ADHA loan details',
                requiredProperties: {
                    propertyId: 'Property id when linked',
                    projectName: 'Project context',
                    locale: 'en | ar',
                },
            },
        },
        'Almetlaa_id_proof_view': {
            eventType: DynatraceEventKind.View,
            userFlowOrder: 156,
            description: 'ADHA id proof when isAdhaJourney',
            detailContext: {
                entity: DynatraceDetailEntity.AdhaIdDocument,
                whatUserViewed: 'ADHA ID proof',
                requiredProperties: {
                    documentType: 'Document type',
                    locale: 'en | ar',
                },
            },
        },
    },
    'loan-details': {
        'loan_details_viewed': {
            eventType: DynatraceEventKind.View,
            userFlowOrder: 153,
            description: 'Native mount — web already tracked',
            detailContext: {
                entity: DynatraceDetailEntity.Loan,
                whatUserViewed: 'Loan details for owned property',
                requiredProperties: {
                    propertyId: 'Property id',
                    propertyName: 'Property name',
                    locale: 'en | ar',
                },
            },
        },
    },
    'id-proof': {
        'id_proof_viewed': {
            eventType: DynatraceEventKind.View,
            userFlowOrder: 155,
            description: 'Mount',
            detailContext: {
                entity: DynatraceDetailEntity.IdDocument,
                whatUserViewed: 'ID proof viewer',
                requiredProperties: {
                    documentType: 'Document type key',
                    locale: 'en | ar',
                },
            },
        },
    },
    'privacy-security': {
        'privacy_security_viewed': {
            eventType: DynatraceEventKind.View,
            userFlowOrder: 157,
            description: 'Mount',
        },
        'change_passcode_started': {
            eventType: DynatraceEventKind.Action,
            userFlowOrder: 158,
            description: 'Navigate to change passcode',
        },
        'privacy_security_change_phone_click': {
            eventType: DynatraceEventKind.Action,
            userFlowOrder: 206,
            description: 'Start change primary phone (web account security)',
        },
        'privacy_security_change_email_click': {
            eventType: DynatraceEventKind.Action,
            userFlowOrder: 207,
            description: 'Start change communication email (web account security)',
        },
    },
    'service-request': {
        'service_request_list_viewed': {
            eventType: DynatraceEventKind.View,
            userFlowOrder: 159,
            description: 'List mount',
        },
        'service_request_open_detail': {
            eventType: DynatraceEventKind.Action,
            userFlowOrder: 160,
            description: 'Tap list row → detail',
        },
    },
    'service-request-detail': {
        'service_request_detail_viewed': {
            eventType: DynatraceEventKind.View,
            userFlowOrder: 161,
            description: 'Detail mount',
            detailContext: {
                entity: DynatraceDetailEntity.ServiceRequest,
                whatUserViewed: 'Support ticket detail',
                requiredProperties: {
                    requestTypeId: 'Request type id',
                    propertyId: 'Related property if any',
                    locale: 'en | ar',
                },
            },
        },
    },
    'service-request-create': {
        'service_request_create_auth_flow_viewed': {
            eventType: DynatraceEventKind.View,
            userFlowOrder: 168,
            description: 'Create screen mount from Emirates OTP raise-support (authFlow=true, not ADHA)',
        },
        'service_request_create_viewed': {
            eventType: DynatraceEventKind.View,
            userFlowOrder: 169,
            description: 'Signed-in create screen mount (non-ADHA, not authFlow)',
        },
        'service_request_submit_click': {
            eventType: DynatraceEventKind.Action,
            userFlowOrder: 194,
            description: 'Submit support request (non-ADHA)',
        },
        'service_request_submit_success': {
            eventType: DynatraceEventKind.Action,
            userFlowOrder: 195,
            description: 'Create API success (non-ADHA)',
        },
        'service_request_submit_error': {
            eventType: DynatraceEventKind.Error,
            userFlowOrder: 196,
            description: 'Create API failure (non-ADHA)',
        },
        'service_request_unit_picker_open': {
            eventType: DynatraceEventKind.Action,
            userFlowOrder: 162,
            description: 'Open unit picker when !isAdhaJourney',
        },
        'service_request_option_picker_select': {
            eventType: DynatraceEventKind.Action,
            userFlowOrder: 163,
            description: 'Pick unit/category in bottom sheet',
        },
    },
    'service-request-success': {
        'service_request_success_dialog_view': {
            eventType: DynatraceEventKind.View,
            userFlowOrder: 164,
            description: 'Inline success dialog',
        },
        'service_request_success_viewed': {
            eventType: DynatraceEventKind.View,
            userFlowOrder: 165,
            description: 'Success mount',
        },
    },
    'Almetlaa-support': {
        'Almetlaa_service_request_create_view': {
            eventType: DynatraceEventKind.View,
            userFlowOrder: 166,
            description: 'Mount when ADHA (no unit picker)',
        },
        'Almetlaa_service_request_submit_click': {
            eventType: DynatraceEventKind.Action,
            userFlowOrder: 167,
            description: 'Submit ADHA service request',
        },
        'Almetlaa_service_request_submit_success': {
            eventType: DynatraceEventKind.Action,
            userFlowOrder: 168,
            description: 'Submit success',
        },
        'Almetlaa_service_request_submit_error': {
            eventType: DynatraceEventKind.Error,
            userFlowOrder: 169,
            description: 'Submit failure',
        },
    },
    'faq': {
        'faq_viewed': {
            eventType: DynatraceEventKind.View,
            userFlowOrder: 170,
            description: 'Native FAQ mount',
        },
        'faq_category_open': {
            eventType: DynatraceEventKind.Action,
            userFlowOrder: 171,
            description: 'Open category',
        },
    },
    'Almetlaa-faq': {
        'Almetlaa_faq_view': {
            eventType: DynatraceEventKind.View,
            userFlowOrder: 172,
            description: 'FAQ mount with ADHA content tag',
        },
        'Almetlaa_faq_category_click': {
            eventType: DynatraceEventKind.Action,
            userFlowOrder: 173,
            description: 'FAQ category expand',
        },
    },
    'legal': {
        'terms_and_conditions_viewed': {
            eventType: DynatraceEventKind.View,
            userFlowOrder: 174,
            description: 'Terms mount',
        },
        'privacy_policy_viewed': {
            eventType: DynatraceEventKind.View,
            userFlowOrder: 175,
            description: 'Privacy mount',
        },
    },
    'logout-success': {
        'logout_success_viewed': {
            eventType: DynatraceEventKind.View,
            userFlowOrder: 176,
            description: 'Post-logout screen',
        },
    },
} as const;

export const DYNATRACE_PAGE_COUNT = 54 as const;
export const DYNATRACE_EVENT_COUNT = 231 as const;

// ---------------------------------------------------------------------------
// App implementation — screens, action names, payload keys (use in trackDynatraceEvent)
// ---------------------------------------------------------------------------

export const ALMETLAA_PREFIX = 'Almetlaa' as const;

/** `p_actionName` segment: `Almetlaa_{suffix}` (lowercase snake only). */
function alm(suffix: string): string {
    return `${ALMETLAA_PREFIX}_${suffix}`;
}

/** `p_screen` buckets — `${ALMETLAA_PREFIX}-…` */
export const ALMETLAA_SCREEN = {
    AUTH: `${ALMETLAA_PREFIX}-auth`,
    CATALOGUE: `${ALMETLAA_PREFIX}-catalogue`,
    UNIT: `${ALMETLAA_PREFIX}-unit`,
    PROJECT: `${ALMETLAA_PREFIX}-project`,
    RESERVE: `${ALMETLAA_PREFIX}-reserve`,
    APPOINTMENT: `${ALMETLAA_PREFIX}-appointment`,
    TOP_UP: `${ALMETLAA_PREFIX}-top-up`,
    PROFILE: `${ALMETLAA_PREFIX}-profile`,
    SUPPORT: `${ALMETLAA_PREFIX}-support`,
    FAQ: `${ALMETLAA_PREFIX}-faq`,
} as const;

/** Static `p_actionName` values (no id segment). */
export const ALMETLAA_ACTION = {
    AUTH: {
        LOGIN_EMIRATES_ID_START_CLICK: `${ALMETLAA_PREFIX}-login-with-emirates-id_start_click`,
        LOGIN_EMIRATES_ID_VIEW: `${ALMETLAA_PREFIX}-login-with-emirates-id_view`,
        LOGIN_EMIRATES_ID_SUBMIT_CLICK: `${ALMETLAA_PREFIX}-login-with-emirates-id_submit_click`,
        LOGIN_EMIRATES_ID_ERROR: `${ALMETLAA_PREFIX}-login-with-emirates-id_error`,
        MOBILE_OTP_VIEW: `${ALMETLAA_PREFIX}-mobile-otp_view`,
        MOBILE_OTP_VERIFY_CLICK: `${ALMETLAA_PREFIX}-mobile-otp_verify_click`,
        MOBILE_OTP_VERIFY_SUCCESS: `${ALMETLAA_PREFIX}-mobile-otp_verify_success`,
        MOBILE_OTP_VERIFY_ERROR: `${ALMETLAA_PREFIX}-mobile-otp_verify_error`,
        EMAIL_OTP_VIEW: `${ALMETLAA_PREFIX}-email-otp_view`,
        EMAIL_OTP_VERIFY_CLICK: `${ALMETLAA_PREFIX}-email-otp_verify_click`,
        EMAIL_OTP_VERIFY_SUCCESS: `${ALMETLAA_PREFIX}-email-otp_verify_success`,
        EMAIL_OTP_VERIFY_ERROR: `${ALMETLAA_PREFIX}-email-otp_verify_error`,
        MOBILE_OTP_RESEND_CLICK: `${ALMETLAA_PREFIX}-mobile-otp_resend_click`,
        MOBILE_OTP_RESEND_ERROR: `${ALMETLAA_PREFIX}-mobile-otp_resend_error`,
        EMAIL_OTP_RESEND_CLICK: `${ALMETLAA_PREFIX}-email-otp_resend_click`,
        EMAIL_OTP_RESEND_ERROR: `${ALMETLAA_PREFIX}-email-otp_resend_error`,
        LOGIN_OTP_RAISE_SUPPORT_CLICK: `${ALMETLAA_PREFIX}-login-otp_raise_support_click`,
        ADHA_SESSION_SUCCESS: `${ALMETLAA_PREFIX}-adha-session_success`,
        ADHA_SESSION_ERROR: `${ALMETLAA_PREFIX}-adha-session_error`,
        UAE_PASS_START_CLICK: `${ALMETLAA_PREFIX}-uae-pass-oauth_start_click`,
        UAE_PASS_SUCCESS: `${ALMETLAA_PREFIX}-uae-pass-oauth_success`,
        UAE_PASS_ERROR: `${ALMETLAA_PREFIX}-uae-pass-oauth_error`,
        UAE_PASS_CANCEL: `${ALMETLAA_PREFIX}-uae-pass-oauth_cancel`,
        UAE_PASS_CALLBACK_VIEW: `${ALMETLAA_PREFIX}-uae-pass-callback_view`,
    },
    CATALOGUE: {
        HOME_VIEW: `${ALMETLAA_PREFIX}_catalogue_home_view`,
        PROJECT_CARD_CLICK: `${ALMETLAA_PREFIX}_project_card_click`,
        UNIT_CARD_CLICK: `${ALMETLAA_PREFIX}_unit_card_click`,
        RESERVATION_CARD_CLICK: `${ALMETLAA_PREFIX}_reservation_card_click`,
        APPOINTMENT_CARD_CLICK: `${ALMETLAA_PREFIX}_appointment_card_click`,
        FILTER_APPLY_CLICK: `${ALMETLAA_PREFIX}_catalogue_filter_apply_click`,
        PAGE_CHANGE: `${ALMETLAA_PREFIX}_catalogue_page_change`,
        PRICE_FILTER_OPEN: `${ALMETLAA_PREFIX}_catalogue_price_filter_open`,
        PRICE_FILTER_APPLY: `${ALMETLAA_PREFIX}_catalogue_price_filter_apply`,
    },
    UNIT: {
        DETAIL_PAGE_TEMPLATE: `${ALMETLAA_PREFIX}_unit_{unitId}_detail_page`,
        GALLERY_OPEN_TEMPLATE: `${ALMETLAA_PREFIX}_unit_{unitId}_gallery_open_click`,
        BROCHURE_DOWNLOAD_TEMPLATE: `${ALMETLAA_PREFIX}_unit_{unitId}_brochure_download_click`,
        RESERVE_CLICK_TEMPLATE: `${ALMETLAA_PREFIX}_unit_{unitId}_reserve_click`,
        BOOK_APPOINTMENT_CLICK_TEMPLATE: `${ALMETLAA_PREFIX}_unit_{unitId}_book_appointment_click`,
        SHARE_CLICK_TEMPLATE: `${ALMETLAA_PREFIX}_unit_{unitId}_share_click`,
        detailPage: (unitId: string) => `${ALMETLAA_PREFIX}_unit_${unitId}_detail_page`,
        reserveClick: (unitId: string) => `${ALMETLAA_PREFIX}_unit_${unitId}_reserve_click`,
        bookAppointmentClick: (unitId: string) =>
            `${ALMETLAA_PREFIX}_unit_${unitId}_book_appointment_click`,
    },
    PROJECT: {
        DETAIL_PAGE_TEMPLATE: `${ALMETLAA_PREFIX}_project_{projectId}_detail_page`,
        detailPage: (projectId: string) => `${ALMETLAA_PREFIX}_project_${projectId}_detail_page`,
        /** Contentful master plan promo banner (web catalogue + native project detail). */
        MASTER_PLAN_BANNER_CLICK: alm('project_master_plan_banner_click'),
        /** Contentful Explore VR promo banner. */
        EXPLORE_VR_BANNER_CLICK: alm('project_explore_vr_banner_click'),
    },
    RESERVE: {
        PAGE_VIEW: alm('reserve_unit_page_view'),
        CONFIRM_CLICK: alm('reserve_unit_confirm_click'),
        SUCCESS: alm('reserve_unit_success'),
        ERROR: alm('reserve_unit_error'),
        ALREADY_RESERVED_ERROR: alm('reserve_unit_already_reserved_error'),
        RESERVATION_SUMMARY_OPEN: alm('reservation_summary_open'),
        CANCEL_DIALOG_OPEN: alm('cancel_reservation_dialog_open'),
        CANCEL_CONFIRM: alm('cancel_reservation_confirm'),
        CANCEL_DIALOG_NO: alm('cancel_reservation_dialog_no'),
        CONFIRM_DIALOG_VIEW: alm('confirm_reservation_dialog_view'),
        CONFIRM_DIALOG_CONFIRM: alm('confirm_reservation_dialog_confirm'),
        CONFIRM_DIALOG_CANCEL: alm('confirm_reservation_dialog_cancel'),
        UNIT_RESERVED_DIALOG_VIEW: alm('unit_reserved_dialog_view'),
        UNIT_RESERVED_SUCCESS_DIALOG_VIEW: alm('unit_reserved_success_dialog_view'),
        UNIT_RESERVED_SUCCESS_PRIMARY_CLICK: alm('unit_reserved_success_primary_click'),
        RESERVATION_CANCELED_DIALOG_VIEW: alm('reservation_canceled_dialog_view'),
        UNIT_NEXT_STEP_SHEET_OPEN: alm('unit_next_step_sheet_open'),
    },
    APPOINTMENT: {
        PAGE_VIEW: alm('book_appointment_page_view'),
        SUBMIT_CLICK: alm('book_appointment_click'),
        SUCCESS: alm('book_appointment_success'),
        ERROR: alm('book_appointment_error'),
        SLOT_SELECT_CLICK: alm('book_appointment_slot_select_click'),
        SLOT_DIALOG_OPEN: alm('appointment_slot_dialog_open'),
        RESCHEDULE_CLICK: alm('reschedule_appointment_click'),
        DATE_SCROLL_CLICK: alm('appointment_date_scroll_click'),
        DATE_SELECT_CLICK: alm('appointment_date_select_click'),
        SLOT_DIALOG_CANCEL: alm('appointment_slot_dialog_cancel'),
        CONFIRMED_DIALOG_VIEW: alm('appointment_confirmed_dialog_view'),
        CANCEL_DIALOG_CONFIRM: alm('cancel_appointment_dialog_confirm'),
        CANCEL_DIALOG_NO: alm('cancel_appointment_dialog_no'),
        CANCEL_REQUEST_CLICK: alm('cancel_appointment_request_click'),
    },
    TOP_UP: {
        COMPLETE_PAYMENT_PAGE_VIEW: alm('complete_payment_page_view'),
        BANK_TRANSFER_SELECT_CLICK: alm('bank_transfer_select_click'),
        PROOF_OF_MORTGAGE_SELECT_CLICK: alm('proof_of_mortgage_select_click'),
        BANK_TRANSFER_DETAILS_VIEW: alm('bank_transfer_details_view'),
        PAYMENT_PROOF_SUBMIT_CLICK: alm('payment_proof_submit_click'),
        SUBMIT_SUCCESS: alm('top_up_submit_success'),
        SUBMIT_ERROR: alm('top_up_submit_error'),
        COMPLETE_PAYMENT_SHEET_OPEN: alm('complete_payment_sheet_open'),
        COMPLETE_PAYMENT_DIALOG_VIEW: alm('complete_payment_dialog_view'),
        COMPLETE_PAYMENT_DIALOG_CONFIRM: alm('complete_payment_dialog_confirm'),
        COMPLETE_PAYMENT_DIALOG_CANCEL: alm('complete_payment_dialog_cancel'),
        BANK_TRANSFER_MADE_CLICK: alm('bank_transfer_made_click'),
        BANK_TRANSFER_DIALOG_VIEW: alm('bank_transfer_dialog_view'),
        PAYMENT_PROOF_RECEIVED_DIALOG_VIEW: alm('payment_proof_received_dialog_view'),
        MOBILE_PAYMENT_FLOW_VIEW: alm('mobile_payment_flow_view'),
    },
    PROFILE: {
        PERSONAL_DETAILS_VIEW: `${ALMETLAA_PREFIX}_personal_details_view`,
        LOAN_DETAILS_VIEW: `${ALMETLAA_PREFIX}_loan_details_view`,
        ID_PROOF_VIEW: `${ALMETLAA_PREFIX}_id_proof_view`,
    },
    SUPPORT: {
        CREATE_VIEW: `${ALMETLAA_PREFIX}_service_request_create_view`,
        SUBMIT_CLICK: `${ALMETLAA_PREFIX}_service_request_submit_click`,
        SUBMIT_SUCCESS: `${ALMETLAA_PREFIX}_service_request_submit_success`,
        SUBMIT_ERROR: `${ALMETLAA_PREFIX}_service_request_submit_error`,
    },
    FAQ: {
        VIEW: `${ALMETLAA_PREFIX}_faq_view`,
        CATEGORY_CLICK: `${ALMETLAA_PREFIX}_faq_category_click`,
    },
} as const;

/**
 * Bloom Universal (web) + Bloom Experience (native) — same landing CMS.
 * Use one naming scheme everywhere (`landing_page_*`), not `bloom_universal_*` / `bloom_experience_*`.
 */
export const LANDING_PAGE_SCREEN = {
    HUB: 'landing-page',
    SPACE: 'landing-page-space',
} as const;

export const LANDING_PAGE_ACTION = {
    VIEWED: 'landing_page_viewed',
    SPACE_VIEWED: 'landing_page_space_viewed',
    HUB_CTA_CLICK: 'landing_page_hub_cta_click',
    EXPLORE_CARD_CLICK: 'landing_page_explore_card_click',
    /** Hub mosaic tile — prefer at runtime: `landing_page_${spaceKey}_click` */
    exploreCardClick: (spaceKey: string) => `landing_page_${spaceKey.trim()}_click`,
    SPACE_DISCOVER_MORE_CLICK: 'landing_page_space_discover_more_click',
    SPACE_INSTITUTION_CARD_CLICK: 'landing_page_space_institution_card_click',
} as const;

/** SDK payload keys — use as `[DT_PAYLOAD.PROJECT_NAME]: value` in trackDynatraceEvent. */
export const DT_PAYLOAD = {
    UNIT_ID: 'unitId',
    UNIT_NAME: 'unitName',
    UNIT_NUMBER: 'unitNumber',
    UNIT_TYPE: 'unitType',
    PROJECT_ID: 'projectId',
    PROJECT_NAME: 'projectName',
    PROPERTY_ID: 'propertyId',
    PROPERTY_NAME: 'propertyName',
    BUILDING_NAME: 'buildingName',
    GUEST_UNIT_NAME: 'guestUnitName',
    LOCATION: 'location',
    LOCALE: 'locale',
    SPACE_KEY: 'spaceKey',
    SPACE_NAME: 'spaceName',
    EXPERIENCE_NAME: 'experienceName',
    BOOKING_ID: 'bookingId',
    APPOINTMENT_ID: 'appointmentId',
    ORDER_REF: 'orderRef',
    TOP_UP_TYPE: 'topUpType',
    ADHA_APPLICATION_NUMBER: 'adhaApplicationNumber',
    ERROR_CODE: 'errorCode',
} as const;

export function buildUnitDisplayName(input: {
    projectName?: string | null;
    unitNumber?: string | null;
    unitType?: string | null;
    buildingName?: string | null;
}): string {
    const project = (input.projectName ?? '').trim();
    const unit = (input.unitNumber ?? '').trim();
    const type = (input.unitType ?? '').trim();
    const building = (input.buildingName ?? '').trim();
    const parts: string[] = [];
    if (project) parts.push(project);
    if (building && building !== project) parts.push(building);
    if (unit) parts.push(`Unit ${unit}`);
    else if (type) parts.push(type);
    return parts.join(' — ') || 'Unknown unit';
}

function collectAlmetlaaActionRefs(
    node: Record<string, unknown>,
    path = 'ALMETLAA_ACTION',
): Record<string, string> {
    const out: Record<string, string> = {};
    for (const [key, value] of Object.entries(node)) {
        const nextPath = `${path}.${key}`;
        if (typeof value === 'string') {
            out[value] = nextPath;
        } else if (value && typeof value === 'object' && !Array.isArray(value)) {
            Object.assign(out, collectAlmetlaaActionRefs(value as Record<string, unknown>, nextPath));
        }
    }
    return out;
}

/** Backlog `p_actionName` → `ALMETLAA_ACTION.*` (auto-built from constants). */
export const ALMETLAA_ACTION_REF_BY_VALUE: Record<string, string> = collectAlmetlaaActionRefs(
    ALMETLAA_ACTION as unknown as Record<string, unknown>,
);
